import format from 'date-fns/format';
import { BasketDetailsModel, BasketItemModel } from 'models/Basket';
import { PlacedOrderModel } from 'models';
import { BasketErrorModel, DEFAULT_ERROR_TEXT } from 'models/Errors';
import { ExponeaCheckoutSteps, getCurrentLocation, DELIVERY_DATE_FORMAT, externalScriptHelper, logsHelper, errorToBasketErrorModel } from 'helpers';
import { PaymentTypes } from 'models/Payment';

export class ExponeaRepository {
    public initialize(): void {
        try {
            const exponea = externalScriptHelper.getExternalScript<Exponea>('exponea');

            if (exponea) {
                exponea.start();
            }
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'exponea.repository:constructor'));
        }
    }

    public addToCart(updatedBasket: BasketDetailsModel, item: BasketItemModel, quantity: number): void {
        try {
            const exponea = this.getExponea();
            const message = this.buildCartUpdateMessage('add', item, quantity, updatedBasket);

            exponea.track('cart_update', message);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'exponea.repository:addToCart'));
        }
    }

    public removeFromCart(updatedBasket: BasketDetailsModel, item: BasketItemModel, quantity: number): void {
        try {
            const exponea = this.getExponea();
            const message = this.buildCartUpdateMessage('remove', item, quantity * -1, updatedBasket);

            exponea.track('cart_update', message);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'exponea.repository:removeFromCart'));
        }
    }

    public identify(userId: string): void {
        try {
            const exponea = this.getExponea();
            exponea.identify(userId);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'exponea.repository:identify'));
        }
    }

    public checkoutStage(step: ExponeaCheckoutSteps, basket: BasketDetailsModel | PlacedOrderModel, paymentType: PaymentTypes | null): void {
        try {
            const exponea = this.getExponea();
            const message = this.getCheckoutStateBaseMessage(step, basket, paymentType);

            exponea.track('checkout', message);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'exponea.repository:checkoutStage'));
        }
    }

    public checkoutStageWithDeliveryDate(step: ExponeaCheckoutSteps, basket: BasketDetailsModel | PlacedOrderModel, paymentType: PaymentTypes, isSelectedQuickestDate: boolean, deliveryDate: Date): void {
        try {
            const exponea = this.getExponea();

            let message = this.getCheckoutStateBaseMessage(step, basket, paymentType);
            message.selected_quickest = isSelectedQuickestDate;
            message.date_selected = format(deliveryDate, DELIVERY_DATE_FORMAT);

            exponea.track('checkout', message);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'exponea.repository:checkoutStageWithDeliveryDate'));
        }
    }

    // commented out as per BCU-461
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public purchase(order: PlacedOrderModel, paymentType: PaymentTypes): void {
        try {
            // const exponea = this.getExponea();

            // const variantList = this.createVariantList(order.entries);

            // const message = {
            //     location: getCurrentLocation(),
            //     purchase_id: order.code,
            //     purchase_status: 'success',
            //     purchase_source_type: order.placementStore === 'INTERNET' ? 'internet' : 'store',
            //     purchase_source_name: order.placementStore.trim().toLowerCase(),
            //     payment_type: paymentType.toLowerCase(),
            //     shipping_cost: order.deliveryCost.value,
            //     shipping_country: order.deliveryAddress.country?.isocode,
            //     shipping_first_name: order.deliveryAddress.firstName,
            //     shipping_last_name: order.deliveryAddress.surname,
            //     shipping_phone: order.deliveryAddress.phone,
            //     shipping_address_line_1: order.deliveryAddress.line1,
            //     shipping_address_line_2: order.deliveryAddress.line2,
            //     shipping_postal_code: order.deliveryAddress.postcode?.code,
            //     shipping_city: order.deliveryAddress.town,
            //     shipping_email: order.deliveryAddress.email,
            //     language: 'en',
            //     total_quantity: order.entries.length,
            //     total_price: order.totalPriceWithTax.value,
            //     variant_ids: order.entries.map(item => item.product.code),
            //     variant_list: variantList
            // };

            // exponea.track('purchase', message);

            // variantList.forEach((item: any) => {
            //     const purchaseItemMessage = { ...message, ...item };

            //     exponea.track('purchase_item', purchaseItemMessage);
            // });
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'exponea.repository:purchase'));
        }
    }

    private getExponea(): Exponea {
        const exponea = externalScriptHelper.getExternalScript<Exponea>('exponea');

        if (!exponea) {
            throw new BasketErrorModel(DEFAULT_ERROR_TEXT, 'Exponea object does not exist in window', 'exponea.repository:getExponea');
        }

        return exponea;
    }

    private getCheckoutStateBaseMessage(step: ExponeaCheckoutSteps, basket: BasketDetailsModel | PlacedOrderModel, paymentType: PaymentTypes | null): any {
        return {
            step_number: step,
            step_title: this.getExponeaStepName(step),
            payment_type: paymentType ? paymentType.toLowerCase() : '',
            location: getCurrentLocation(),
            variant_list: this.createVariantList(basket.entries),
            variant_ids: basket.entries.map(item => item.product.code),
            total_quantity: basket.entries.length,
            total_price: basket.totalPriceWithTax?.value,
        };
    }

    private createVariantList(entries: BasketItemModel[]): any {
        return entries.map((item: BasketItemModel) => {
            const categories = item.product.categories || [];

            return {
                product_type: this.getExponeaProductTypeNameByVariant(item.product.baseOptions[0].variantType),
                variant_id: item.product.code,
                bcop: item.bcop ?? '',
                swatch: item.swatchName ?? '',
                colour: item.swatchColour ?? '',
                category_1: categories.length > 0 ? categories[0]?.code : '',
                categories: categories.map(category => category.code),
                range: item.range ?? '',
                price: Math.floor(item.totalPrice.value / item.quantity),
                total_quantity: item.quantity,
                total_price: item.totalPrice.value
            };
        });
    }

    private getExponeaStepName(step: ExponeaCheckoutSteps): string {
        switch (step) {
            case ExponeaCheckoutSteps.DeliveryDetails:
                return 'address';
            case ExponeaCheckoutSteps.DeliveryDate:
                return 'eca';
            case ExponeaCheckoutSteps.DataProtectionNotice:
                return 'dpn';
            case ExponeaCheckoutSteps.Finance:
                return 'finance';
            case ExponeaCheckoutSteps.FinanceProcessing:
                return 'finance status';
            case ExponeaCheckoutSteps.Payment:
                return 'payment';
            case ExponeaCheckoutSteps.OrderProcessing:
                return 'order processing';
            case ExponeaCheckoutSteps.Summary:
                return 'summary';
        }
    }

    private getExponeaProductTypeNameByVariant(variant: string): string | undefined {
        switch (variant) {
            case 'Variant':
                return 'UPHOLSTERY';
            case 'VariantProduct':
                return 'OCCASIONAL';
            case 'GenericVariantProduct':
                return 'UPSELL';
            default:
                return undefined;
        }
    }

    private buildCartUpdateMessage(action: 'add' | 'remove', item: BasketItemModel, quantity: number, basket: BasketDetailsModel): any {
        const singleItemPrice = Math.floor(item.totalPrice.value / item.quantity);
        const variantType = item.product.baseOptions[0]?.variantType;
        const productType = this.getExponeaProductTypeNameByVariant(variantType);
        const categories = item.product.categories || [];

        return {
            action,
            channel: 'INTERNET',
            source: 'INTERNET',
            category_1: categories.length ? categories[0].code : undefined,
            categories: categories.map(cat => cat.code),
            bcop: item.bcop || '',
            swatch: item.swatchName || '',
            colour: item.swatchColour,
            product_type: productType,
            range: this.cleanRangeName(item.range) || 'SOFOLOGY',
            price: singleItemPrice,
            total_quantity: Math.abs(quantity),
            variant_id: item.product.code,
            variant_ids: (basket.entries || []).map(entry => entry.product.code),
            variant_list: (basket.entries || []).map(entry => this.buildVariantListItem(entry)),
            total_price: basket.totalPriceWithTax.value,
        };
    }

    private buildVariantListItem(entry: BasketItemModel): any {
        const categories = entry.product.categories || [];
        const variantType = entry.product.baseOptions[0]?.variantType;

        return {
            product_type: this.getExponeaProductTypeNameByVariant(variantType),
            variant_id: entry.product.code,
            bcop: entry.bcop || '',
            swatch: entry.swatchName || '',
            colour: entry.swatchColour,
            category_1: categories.length ? categories[0].code : undefined,
            categories: categories.map(cat => cat.code),
            range: entry.range,
            price: Math.floor(entry.totalPrice.value / entry.quantity),
            total_quantity: entry.quantity,
            total_price: entry.totalPrice.value,
        };
    }

    private cleanRangeName(rangeName: string | null | undefined): string {
        if (!rangeName) {
            return 'SOFOLOGY';
        }

        const upperCaseRangeName = rangeName.toUpperCase();
        const cleanedRangeName = upperCaseRangeName
            .replace(/%20/g, ' ')
            .replace(/-/g, ' ');

        return cleanedRangeName;
    }
}

interface Exponea {
    identify: (userId: string) => void;
    anonymize: () => void;
    track: (eventName: string, message: any) => void;
    start: () => void;
}

export const exponea = new ExponeaRepository();